import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Routes } from 'constants/routes.constants';

import Link from 'components/@common/Link';
import ResponsiveDrawerDialog from 'components/@common/ResponsiveDrawerDialog';
import Switch from 'components/@common/Switch';

import {
  customizeButtonSx,
  descriptionSx,
  dialogPaperSx,
  titleSx,
} from './CookieNotice.styles';

type CookieNoticeView = 'notice' | 'customize';

type CookieNoticeCategory = {
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
};

type CookieNoticeProps = {
  open: boolean;
  view: CookieNoticeView;
  categories: Array<CookieNoticeCategory>;
  showBack?: boolean;
  showClose?: boolean;
  onAllowAll: () => void;
  onDecline: () => void;
  onCustomize: () => void;
  onBack?: () => void;
  onDismiss: () => void;
  onSave: () => void;
};

// Use app Link + Routes so Next prefixes the active locale (/ar/...).
const PolicyLink = ({ children }: { children: ReactNode }) => (
  <Link
    href={Routes.PrivacyPolicy} // TODO: replace with actual cookie policy link once it is available
    underline="always"
    color="inherit"
    variant="small"
    target="_blank"
    rel="noopener noreferrer"
  >
    {children}
  </Link>
);

const NoticeActions = ({
  onAllowAll,
  onDecline,
  onCustomize,
}: {
  onAllowAll: () => void;
  onDecline: () => void;
  onCustomize: () => void;
}) => (
  <>
    <Typography variant="small" sx={descriptionSx}>
      <FormattedMessage
        id="cookie.notice.description"
        values={{
          a: (chunks: Array<ReactNode>) => <PolicyLink>{chunks}</PolicyLink>,
        }}
      />
    </Typography>

    <Stack
      sx={{
        gap: 3,
        alignItems: 'center',
        width: '100%',
        alignSelf: 'stretch',
      }}
    >
      <Stack
        sx={{
          gap: 1.5,
          width: '100%',
        }}
      >
        <Button
          variant="outlined"
          color="secondary"
          fullWidth
          onClick={onAllowAll}
        >
          <FormattedMessage id="cookie.notice.allow_all" />
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={onCustomize}
          sx={customizeButtonSx}
        >
          <FormattedMessage id="cookie.notice.customize" />
        </Button>
      </Stack>
      <Button variant="text" color="primary" size="small" onClick={onDecline}>
        <FormattedMessage id="cookie.notice.decline" />
      </Button>
    </Stack>
  </>
);

const CustomizeActions = ({
  categories,
  onSave,
}: {
  categories: Array<CookieNoticeCategory>;
  onSave: () => void;
}) => (
  <>
    <Stack sx={{ gap: 2, width: '100%', alignSelf: 'stretch' }}>
      {categories.map((category) => (
        <Box
          key={category.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            alignSelf: 'stretch',
            gap: 2,
          }}
        >
          <Typography
            variant="body1"
            sx={{ fontWeight: 700, color: 'text.primary' }}
          >
            {category.label}
          </Typography>
          <Switch
            checked={category.checked}
            disabled={category.disabled}
            onChange={(_event, checked) => category.onChange?.(checked)}
            slotProps={{
              input: {
                'aria-label': category.label,
              },
            }}
          />
        </Box>
      ))}
    </Stack>

    <Stack
      sx={{
        gap: { xs: 3, md: 2 },
        alignItems: 'center',
        width: '100%',
        alignSelf: 'stretch',
      }}
    >
      <Button variant="outlined" color="secondary" fullWidth onClick={onSave}>
        <FormattedMessage id="cookie.notice.save" />
      </Button>
      <PolicyLink>
        <FormattedMessage id="cookie.notice.policy_link" />
      </PolicyLink>
    </Stack>
  </>
);

const CookieNotice = ({
  open,
  view,
  categories,
  showBack = false,
  showClose = false,
  onAllowAll,
  onDecline,
  onCustomize,
  onBack,
  onDismiss,
  onSave,
}: CookieNoticeProps) => {
  const isCustomize = view === 'customize';
  const hasHeaderActions = showBack || showClose;

  return (
    <ResponsiveDrawerDialog
      isOpen={open}
      onBack={showBack ? onBack : undefined}
      onClose={showClose ? onDismiss : undefined}
      dialogProps={{
        disableEscapeKeyDown: true,
        maxWidth: false,
        onClose: (_event, reason) => {
          if (reason === 'backdropClick') return;
        },
        slotProps: {
          paper: { sx: dialogPaperSx },
        },
      }}
      drawerProps={{
        ModalProps: { disableEscapeKeyDown: true },
      }}
    >
      <DialogTitle
        sx={(theme) => ({
          '&&': {
            pt: { xs: 9, md: 3 },
            pb: 0,
            ...(!hasHeaderActions && {
              px: { xs: 2, md: 3 },
            }),
            // Mobile: title is centered on its own row under absolute chrome.
            // One-sided back/close clearance skews centering; use full width.
            // Desktop: keep RDD-style clearance beside absolute buttons.
            ...(hasHeaderActions && {
              px: { xs: 2 },
              [theme.breakpoints.up('md')]: {
                paddingLeft: theme.spacing(showBack ? 12 : 3),
                paddingRight: theme.spacing(showClose ? 10 : 3),
              },
            }),
          },
        })}
      >
        <Typography component="span" sx={titleSx}>
          <FormattedMessage
            id={
              isCustomize
                ? 'cookie.notice.customize.title'
                : 'cookie.notice.title'
            }
          />
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          px: { xs: 2, md: 3 },
          pt: 3,
          pb: { xs: 5, md: 3 },
        }}
      >
        <Stack
          sx={{
            pt: 3,
            gap: { xs: 5, md: 3 },
            alignItems: { xs: 'center', md: 'stretch' },
            width: '100%',
          }}
        >
          {isCustomize ? (
            <CustomizeActions categories={categories} onSave={onSave} />
          ) : (
            <NoticeActions
              onAllowAll={onAllowAll}
              onDecline={onDecline}
              onCustomize={onCustomize}
            />
          )}
        </Stack>
      </DialogContent>
    </ResponsiveDrawerDialog>
  );
};

export default CookieNotice;
